import React from "react";
import { Modal } from "@material-ui/core";
import styled, { keyframes } from "styled-components";
import { navigate } from "@reach/router";

const DayEventsModal = ({ open, onClose, day, events }) => {
  function convertTime(time24) {
    if (!time24) return "N/A";
    const [hours, minutes] = time24.split(":");
    let time12 = new Date(2024, 1, 1, hours, minutes);
    time12 = time12.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    return time12;
  }

  return (
    <Modal open={open} disableRestoreFocus={true} onClose={onClose}>
      <ModalWrapper>
        <ModalCard>
          <ModalHeader>
            <HeaderGradient />
            <HeaderContent>
              <DateBadge>
                <DayNumber>{day?.format("DD")}</DayNumber>
                <DayMonth>{day?.format("MMM")}</DayMonth>
              </DateBadge>
              <HeaderText>
                <ModalTitle>{day?.format("dddd, MMMM D, YYYY")}</ModalTitle>
                <CaseCount>
                  {events?.length} Scheduled Case
                  {events?.length !== 1 ? "s" : ""}
                </CaseCount>
              </HeaderText>
            </HeaderContent>
          </ModalHeader>

          <ModalBody>
            {events?.length === 0 ? (
              <EmptyState>
                <EmptyIcon>event_busy</EmptyIcon>
                <EmptyText>No cases scheduled for this day</EmptyText>
              </EmptyState>
            ) : (
              <EventsList>
                {events?.map((evt, idx) => (
                  <EventItem
                    key={idx}
                    onClick={() => {
                      navigate(
                        `/mediator/cases/${evt.caseId}?caseType=${evt.resolutionKind}`,
                      );
                      onClose();
                    }}
                  >
                    <EventNumber>{idx + 1}</EventNumber>
                    <EventDetails>
                      <EventTitle>{evt.title}</EventTitle>
                      <EventMeta>
                        <MetaItem>
                          <MetaIcon>confirmation_number</MetaIcon>
                          <MetaText>{evt.caseId}</MetaText>
                        </MetaItem>
                        <MetaItem>
                          <MetaIcon>schedule</MetaIcon>
                          <MetaText>{convertTime(evt.time)}</MetaText>
                        </MetaItem>
                      </EventMeta>
                      {evt.description && (
                        <EventDescription>{evt.description}</EventDescription>
                      )}
                    </EventDetails>
                    <ArrowIcon>arrow_forward</ArrowIcon>
                  </EventItem>
                ))}
              </EventsList>
            )}
          </ModalBody>

          <ModalFooter>
            <FooterButton onClick={onClose}>Close</FooterButton>
          </ModalFooter>
        </ModalCard>
      </ModalWrapper>
    </Modal>
  );
};

export default DayEventsModal;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 700px;
  animation: ${fadeIn} 0.3s ease-out;
  outline: none;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(41, 52, 97, 0.3);
  overflow: hidden;
  border: 1px solid rgba(0, 131, 140, 0.1);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 2rem 2.5rem;
  overflow: hidden;
  flex-shrink: 0;
`;

const HeaderGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #e1e3ee 0%, #e1e3ee 100%);

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const DateBadge = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DayNumber = styled.div`
  font-size: 2rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #00838c;
  line-height: 1;
`;

const DayMonth = styled.div`
  font-size: 0.875rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #293461;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 0.25rem;
`;

const HeaderText = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #293461;
  letter-spacing: 0.5px;
`;

const CaseCount = styled.div`
  font-size: 0.95rem;
  font-family: openSans-Regular, sans-serif;
  color: #293461;
`;

const ModalBody = styled.div`
  padding: 0;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
  }
`;

const EventsList = styled.div`
  padding: 1.5rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EventItem = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%);
  border: 2px solid #e5e9f2;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 131, 140, 0.15);
    transform: translateX(4px);
  }
`;

const EventNumber = styled.div`
  background: #00838c;
  color: white;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.125rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 131, 140, 0.3);
`;

const EventDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const EventTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.125rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #293461;
`;

const EventMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetaIcon = styled.span`
  font-family: "Material Icons";
  font-size: 1rem;
  color: #00838c;
`;

const MetaText = styled.span`
  font-size: 0.875rem;
  font-family: openSans-Regular, sans-serif;
  color: #6b7280;
`;

const EventDescription = styled.p`
  margin: 0.75rem 0 0 0;
  font-size: 0.875rem;
  font-family: openSans-Regular, sans-serif;
  color: #4b5563;
  line-height: 1.6;
  padding-left: 1rem;
  border-left: 3px solid rgba(0, 131, 140, 0.3);
`;

const ArrowIcon = styled.span`
  font-family: "Material Icons";
  color: #00838c;
  font-size: 1.5rem;
  flex-shrink: 0;
  transition: transform 0.3s ease;

  ${EventItem}:hover & {
    transform: translateX(4px);
  }
`;

const EmptyState = styled.div`
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const EmptyIcon = styled.span`
  font-family: "Material Icons";
  font-size: 4rem;
  color: #d1d5db;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  font-family: openSans-Regular, sans-serif;
  color: #6b7280;
  margin: 0;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2.5rem;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 131, 140, 0.05) 100%
  );
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid rgba(0, 131, 140, 0.1);
  flex-shrink: 0;
`;

const FooterButton = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #d91b1b;
  background: #d91b1b;
  color: #ffffff;

  &:hover {
    border-color: #d91b1b;
  }

  &:active {
    transform: translateY(0);
  }
`;
